
import React, { useState, useEffect } from 'react';
import { useLocation, useNavigate } from 'react-router-dom';
import { motion } from 'framer-motion';
import { useTeacherAuth } from '../auth/TeacherAuthContext'; 
import {
  Container,
  Typography,
  Box,
  CircularProgress,
  Button,
  Table,
  TableBody,
  TableCell,
  TableContainer,
  TableHead,
  TableRow,
  Paper,
  Chip,
  Accordion,
  AccordionSummary,
  AccordionDetails,
  Select,
  MenuItem,
  FormControl,
  InputLabel,
} from '@mui/material';
import { ExpandMore } from '@mui/icons-material';
import axios from 'axios';
import { Pie, Bar } from 'react-chartjs-2';
import { Chart as ChartJS, ArcElement, Tooltip, Legend, BarElement, CategoryScale, LinearScale } from 'chart.js';
import jsPDF from 'jspdf';
import 'jspdf-autotable';
import * as XLSX from 'xlsx';
import htmlDocx from 'html-docx-js/dist/html-docx';

// Register Chart.js components
ChartJS.register(ArcElement, Tooltip, Legend, BarElement, CategoryScale, LinearScale);

// Helper functions
const getShortSubjectName = subject => {
  const shortNames = {
    Language: 'Lang',
    Mathematics: 'Math',
    'Psychomotor and Creative Arts': 'PCA',
    Environmental: 'Env',
    'Religious Education': 'RE',
    'English Language': 'Eng',
    'English Composition': 'Comp',
    'Kiswahili Lugha': 'Kisw',
    'Kiswahili Insha': 'Insha',
    'Science and Technology': 'Sci/Tech',
    'Science & Technology': 'Sci/Tech',
    'Agriculture and Nutrition': 'Agri/Nut',
    Agriculture: 'Agri',
    'Social Studies': 'SS',
    'Creative Arts & Sports': 'CAS',
    'Creative Arts': 'Arts',
    Science: 'Sci',
    'Pre-Technical Studies': 'PTS',
    'Pre-Technical': 'Pre-Tech',
  };
  return shortNames[subject] || subject;
};

const getExamDisplayTitle = (examType, examName) => {
  if (['Random Exams', 'Other Exams'].includes(examType) && examName) {
    return `${examType}: ${examName}`;
  }
  return examType;
};

const mapSubjectsToDisplay = (subjects, className) => {
  const displayOrder = {
    'Grade 4': [
      'English Language',
      'English Composition',
      'Kiswahili Lugha',
      'Kiswahili Insha',
      'Mathematics',
      'Science & Technology',
      'Agriculture',
      'Social Studies',
      'Religious Education',
      'Creative Arts',
    ],
    'Grade 5': [
      'English Language',
      'English Composition',
      'Kiswahili Lugha',
      'Kiswahili Insha',
      'Mathematics',
      'Science & Technology',
      'Agriculture',
      'Social Studies',
      'Religious Education',
      'Creative Arts',
    ],
    'Grade 6': [
      'English Language',
      'English Composition',
      'Kiswahili Lugha',
      'Kiswahili Insha',
      'Mathematics',
      'Science & Technology',
      'Agriculture',
      'Social Studies',
      'Religious Education',
      'Creative Arts',
    ],
    'Grade 7': [
      'English Language',
      'English Composition',
      'Kiswahili Lugha',
      'Kiswahili Insha',
      'Mathematics',
      'Science & Technology',
      'Agriculture',
      'Social Studies',
      'Religious Education',
      'Creative Arts',
      'Pre-Technical',
    ],
    'Grade 8': [
      'English Language',
      'English Composition',
      'Kiswahili Lugha',
      'Kiswahili Insha',
      'Mathematics',
      'Science & Technology',
      'Agriculture',
      'Social Studies',
      'Religious Education',
      'Creative Arts',
      'Pre-Technical',
    ],
    'Grade 9': [
      'English Language',
      'English Composition',
      'Kiswahili Lugha',
      'Kiswahili Insha',
      'Mathematics',
      'Science & Technology',
      'Agriculture',
      'Social Studies',
      'Religious Education',
      'Creative Arts',
      'Pre-Technical',
    ],
  };

  const subjectMapping = {
    'Science and Technology': 'Science & Technology',
    'Agriculture and Nutrition': 'Agriculture',
    'Creative Arts & Sports': 'Creative Arts',
    'Pre-Technical Studies': 'Pre-Technical',
  };

  if (displayOrder[className]) {
    return displayOrder[className]
      .map(displaySubject => {
        const dataSubject = Object.keys(subjectMapping).find(key => subjectMapping[key] === displaySubject) || displaySubject;
        return subjects.includes(dataSubject) ? { dataSubject, displaySubject } : null;
      })
      .filter(Boolean);
  }

  return subjects.map(dataSubject => ({
    dataSubject,
    displaySubject: subjectMapping[dataSubject] || dataSubject,
  }));
};

const PerformanceAnalysis = () => {
  const location = useLocation();
  const navigate = useNavigate();
  const { schoolId: contextSchoolId } = useTeacherAuth();
  const { grade, term, examType, examName, data, schoolId = contextSchoolId } = location.state || {};
  const dataSubjects = Array.from(data?.subjects || []);
  const subjectMap = mapSubjectsToDisplay(dataSubjects, grade);
  const subjects = subjectMap.map(item => item.dataSubject);
  const displaySubjects = subjectMap.map(item => item.displaySubject);
  const [topN, setTopN] = useState(1); // State for dropdown selection

  const [isGenerating, setIsGenerating] = useState({ general: false, streams: {} });
  const [schoolDetails, setSchoolDetails] = useState({
    schoolName: '',
    schoolCode: '',
    county: '',
    subcounty: '',
    location: '',
    village: '',
    address: '',
    phoneNumber: '',
    email: '',
    website: '',
  });
  const [learners, setLearners] = useState([]);
  const [streams, setStreams] = useState([]);
  const [isLoading, setIsLoading] = useState(true);

  useEffect(() => {
    const fetchSchoolDetails = async () => {
      try {
        const response = await axios.get('https://kidsm.vercel.app/api/schools/profile', { params: { schoolId } });
        setSchoolDetails({
          schoolName: response.data.schoolName || 'Unknown School',
          schoolCode: response.data.schoolCode || 'Unknown Code',
          county: response.data.county || 'Unknown County',
          subcounty: response.data.subcounty || 'Unknown Subcounty',
          location: response.data.location || 'Unknown Location',
          village: response.data.village || 'Unknown Village',
          address: response.data.address || 'Unknown Address',
          phoneNumber: response.data.phoneNumber || 'Unknown Contact',
          email: response.data.email || 'Unknown Email',
          website: response.data.website || 'Not Specified',
        });
      } catch (error) {
        console.error('Error fetching school details:', error);
      }
    };

    const fetchLearners = async () => {
      try {
        const response = await axios.get('https://kidsm.vercel.app/api/schools/learners', { params: { schoolId } });
        const gradeLearners = response.data.filter(learner => learner.class === grade);
        setLearners(gradeLearners);
        const gradeStreams = [...new Set(gradeLearners.map(learner => learner.stream).filter(Boolean))].sort();
        setStreams(gradeStreams);
      } catch (error) {
        console.error('Error fetching learners:', error);
      } finally {
        setIsLoading(false);
      }
    };

    fetchSchoolDetails();
    fetchLearners();
  }, [schoolId, grade]);

  const calculatePositions = learners => {
    let currentPosition = 1;
    let previousTotal = null;
    let samePositionCount = 0;

    return learners.map((learner, index) => {
      if (index === 0) {
        previousTotal = learner.total;
        return { ...learner, position: currentPosition };
      }
      if (learner.total === previousTotal) {
        samePositionCount++;
        return { ...learner, position: currentPosition };
      } else {
        currentPosition += samePositionCount + 1;
        samePositionCount = 0;
        previousTotal = learner.total;
        return { ...learner, position: currentPosition };
      }
    });
  };

  const processedLearners = calculatePositions(
    (data?.learners || [])
      .map(learner => {
        const marks = subjects.reduce((acc, subject) => {
          acc[subject] = learner.marks[subject] || 0;
          return acc;
        }, {});
        const total = Object.values(marks).reduce((sum, mark) => sum + mark, 0);
        const validMarks = Object.values(marks).filter(mark => mark > 0);
        const mean = validMarks.length > 0 ? Number((total / validMarks.length).toFixed(2)) : 0;
        const learnerData = learners.find(l => l._id === learner.learnerId);
        const stream = learnerData?.stream || 'No Stream';
        return { ...learner, marks, total, mean, stream };
      })
      .sort((a, b) => b.total - a.total)
  );

  const learnersByStream = streams.reduce((acc, stream) => {
    acc[stream] = calculatePositions(
      processedLearners.filter(learner => learner.stream === stream).sort((a, b) => b.total - a.total)
    );
    return acc;
  }, {});
  learnersByStream['General'] = processedLearners;

  const streamAverages = Object.entries(learnersByStream)
    .map(([stream, learners]) => ({
      stream,
      average: learners.length > 0 ? Number((learners.reduce((sum, learner) => sum + learner.total, 0) / learners.length).toFixed(2)) : 0,
      count: learners.length,
    }))
    .sort((a, b) => b.average - a.average);

  const subjectAverages = subjects.map((subject, index) => {
    const marks = processedLearners.map(learner => learner.marks[subject] || 0).filter(mark => mark > 0);
    const average = marks.length > 0 ? Number((marks.reduce((sum, mark) => sum + mark, 0) / marks.length).toFixed(2)) : 0;
    return { subject: displaySubjects[index], dataSubject: subject, average, count: marks.length };
  });

  const subjectAveragesByStream = streams.reduce((acc, stream) => {
    const streamLearners = learnersByStream[stream];
    acc[stream] = subjects.map((subject, index) => {
      const marks = streamLearners.map(learner => learner.marks[subject] || 0).filter(mark => mark > 0);
      const average = marks.length > 0 ? Number((marks.reduce((sum, mark) => sum + mark, 0) / marks.length).toFixed(2)) : 0;
      return { subject: displaySubjects[index], dataSubject: subject, average, count: marks.length };
    });
    return acc;
  }, {});

  const topPerformers = processedLearners.slice(0, 5);

  const topStudentsBySubject = subjects.reduce((acc, subject, index) => {
    const sortedLearners = processedLearners
      .filter(learner => learner.marks[subject] > 0)
      .sort((a, b) => b.marks[subject] - a.marks[subject]);
    const topScores = [...new Set(sortedLearners.map(l => l.marks[subject]))].slice(0, topN);
    acc[displaySubjects[index]] = sortedLearners
      .filter(learner => topScores.includes(learner.marks[subject]))
      .map(learner => ({ name: learner.learnerName, score: learner.marks[subject], stream: learner.stream, learnerId: learner.learnerId }));
    return acc;
  }, {});

  const pieData = {
    labels: subjectAverages.map(item => getShortSubjectName(item.subject)),
    datasets: [{
      data: subjectAverages.map(item => item.average),
      backgroundColor: subjectAverages.map((_, index) => `hsl(${(index * 360) / (subjects.length || 1)}, 65%, 55%)`),
    }],
  };

  const barData = {
    labels: displaySubjects.map(sub => getShortSubjectName(sub)),
    datasets: [{
      label: 'Average (%)',
      data: subjectAverages.map(item => item.average || 0),
      backgroundColor: '#3182CE',
    }],
  };

  const prepareSubjectComparisonData = displaySubject => {
    const subjectIndex = displaySubjects.indexOf(displaySubject);
    const dataSubject = subjects[subjectIndex];
    return {
      labels: streams,
      datasets: [{
        data: streams.map(stream =>
          subjectAveragesByStream[stream]?.find(item => item.dataSubject === dataSubject)?.average || 0
        ),
        backgroundColor: streams.map((_, index) => `hsl(${(index * 360) / (streams.length || 1)}, 65%, 55%)`),
      }],
    };
  };

  const calculateMeanRow = learners => {
    const meanData = { learnerName: 'Mean', marks: {}, total: 0, displayMarks: {} };
    subjects.forEach((subject, index) => {
      const subjectMarks = learners.map(learner => learner.marks[subject] || 0).filter(mark => mark > 0);
      const sum = subjectMarks.reduce((a, b) => a + b, 0);
      meanData.marks[subject] = Number((sum / (subjectMarks.length || 1)).toFixed(2)) || 0;
      meanData.displayMarks[displaySubjects[index]] = meanData.marks[subject];
    });
    const totals = learners.map(learner => learner.total);
    meanData.total = Number((totals.reduce((a, b) => a + b, 0) / (learners.length || 1)).toFixed(2)) || 0;
    return meanData;
  };

  const generateCSVContent = (learners, stream, type) => {
    const sections = [
      [`School: ${schoolDetails.schoolName}`],
      [`School Code: ${schoolDetails.schoolCode}`],
      [`Address: ${[schoolDetails.village, schoolDetails.location, schoolDetails.subcounty, schoolDetails.county].filter(Boolean).join(', ')}`],
      [`Physical Address: ${schoolDetails.address}`],
      [`Contact: ${schoolDetails.phoneNumber}`],
      [`Email: ${schoolDetails.email}`],
      [`Website: ${schoolDetails.website}`],
      [''],
      [`${grade} - Term ${term} - ${getExamDisplayTitle(examType, examName)}`],
      [''],
    ];

    if (type === 'general' && streamAverages.length > 0) {
      sections.push(['Performance Overview - Stream Rankings']);
      sections.push(['Rank', 'Stream', 'Average (%)', 'Students']);
      streamAverages.forEach(({ stream, average, count }, index) => {
        sections.push([`#${index + 1}`, stream, average, count]);
      });
      sections.push(['']);
    }

    sections.push(['Performance Overview - Subject Averages']);
    sections.push(['Subject', 'Average (%)', 'Students']);
    subjectAverages.forEach(({ subject, average, count }) => {
      sections.push([getShortSubjectName(subject), average, count]);
    });
    sections.push(['']);

    if (type === 'general' && streams.length > 0) {
      sections.push(['Subject Analysis by Stream']);
      displaySubjects.forEach(subject => {
        sections.push([getShortSubjectName(subject)]);
        sections.push(['Stream', 'Average (%)', 'Students']);
        streams.sort().forEach(stream => {
          const streamAvg = subjectAveragesByStream[stream].find(item => item.subject === subject);
          sections.push([stream, streamAvg?.average || 0, streamAvg?.count || 0]);
        });
        sections.push(['']);
      });
    }

    sections.push(['Top Performers']);
    sections.push(['Position', 'Student', 'Total Marks', 'Mean (%)', 'Stream', ...displaySubjects.map(sub => getShortSubjectName(sub))]);
    topPerformers.forEach(performer => {
      sections.push([
        performer.position,
        `${performer.learnerName}${performer.stream ? ` (${performer.stream})` : ''}`,
        performer.total,
        performer.mean,
        performer.stream || 'No Stream',
        ...subjects.map(subject => performer.marks[subject] || 'N/A'),
      ]);
    });
    sections.push(['']);

    sections.push(['Top Students by Subject']);
    displaySubjects.forEach(subject => {
      sections.push([getShortSubjectName(subject)]);
      sections.push(['Student', 'Score (%)', 'Stream']);
      topStudentsBySubject[subject].forEach(student => {
        sections.push([`${student.name}${student.stream ? ` (${student.stream})` : ''}`, student.score, student.stream || 'No Stream']);
      });
      sections.push(['']);
    });

    sections.push([type === 'general' ? 'Detailed Results - All Students' : `Detailed Results - Stream ${stream}`]);
    const headers = ['Position', 'Student', ...displaySubjects.map(sub => getShortSubjectName(sub)), 'Total', ...(type === 'general' ? ['Stream Position', 'Stream'] : [])];
    const rows = learners.map(learner => [
      learner.position,
      learner.learnerName,
      ...subjects.map(subject => learner.marks[subject] || '-'),
      learner.total,
      ...(type === 'general' ? [
        learnersByStream[learner.stream]?.find(l => l.learnerId === learner.learnerId)?.position || '-',
        learner.stream || 'No Stream',
      ] : []),
    ]);
    const meanRow = calculateMeanRow(learners);
    const meanRowData = ['-', 'Mean', ...subjects.map(subject => `${meanRow.marks[subject]}`), meanRow.total, ...(type === 'general' ? ['-', '-'] : [])];
    sections.push(headers);
    sections.push(...rows);
    sections.push(meanRowData);

    if (type === 'general') {
      streams.sort().forEach(stream => {
        sections.push(['']);
        sections.push([`Detailed Results - Stream ${stream}`]);
        const streamHeaders = ['Position', 'Student', ...displaySubjects.map(sub => getShortSubjectName(sub)), 'Total'];
        const streamRows = learnersByStream[stream].map(learner => [
          learner.position,
          learner.learnerName,
          ...subjects.map(subject => learner.marks[subject] || '-'),
          learner.total,
        ]);
        const streamMeanRow = calculateMeanRow(learnersByStream[stream]);
        const streamMeanRowData = ['-', 'Mean', ...subjects.map(subject => `${streamMeanRow.marks[subject]}`), streamMeanRow.total];
        sections.push(streamHeaders);
        sections.push(...streamRows);
        sections.push(streamMeanRowData);
      });
    }

    return sections.map(row => row.join(',')).join('\n');
  };

  const generateDocxContent = (learners, stream, type) => {
    return `
      <div style="font-family: Arial, sans-serif; padding: 30px;">
        <h1 style="text-align: center; font-size: 24px;">${schoolDetails.schoolName}</h1>
        <h2 style="font-size: 20px; margin-bottom: 10px;">${grade} - Term ${term} - ${getExamDisplayTitle(examType, examName)} - ${type === 'general' ? 'General Results' : `Stream ${stream}`}</h2>
        <table style="width: 100%; border-collapse: collapse; margin-top: 12px;">
          <thead>
            <tr>
              <th style="background: #3182CE; color: #FFFFFF; padding: 12px; text-align: center;">Position</th>
              <th style="background: #3182CE; color: #FFFFFF; padding: 12px; text-align: center;">Student</th>
              ${displaySubjects.map(subject => `<th style="background: #3182CE; color: #FFFFFF; padding: 12px; text-align: center;">${getShortSubjectName(subject)}</th>`).join('')}
              <th style="background: #3182CE; color: #FFFFFF; padding: 12px; text-align: center;">Total</th>
              ${stream === 'General' ? '<th style="background: #3182CE; color: #FFFFFF; padding: 12px; text-align: center;">Stream Position</th><th style="background: #3182CE; color: #FFFFFF; padding: 12px; text-align: center;">Stream</th>' : ''}
            </tr>
          </thead>
          <tbody>
            ${learners.map(learner => `
              <tr>
                <td style="padding: 12px; text-align: center; border: 1px solid #E2E8F0;">${learner.position}</td>
                <td style="padding: 12px; text-align: center; border: 1px solid #E2E8F0;">${learner.learnerName}${stream === 'General' && learner.stream ? ` (${learner.stream})` : ''}</td>
                ${subjects.map(subject => `<td style="padding: 12px; text-align: center; border: 1px solid #E2E8F0;">${learner.marks[subject] || '-'}</td>`).join('')}
                <td style="padding: 12px; text-align: center; border: 1px solid #E2E8F0;">${learner.total}</td>
                ${stream === 'General' ? `<td style="padding: 12px; text-align: center; border: 1px solid #E2E8F0;">${learnersByStream[learner.stream]?.find(l => l.learnerId === learner.learnerId)?.position || '-'}</td><td style="padding: 12px; text-align: center; border: 1px solid #E2E8F0;">${learner.stream || 'No Stream'}</td>` : ''}
              </tr>
            `).join('')}
            <tr style="background-color: #F0F9FF; font-weight: bold;">
              <td style="padding: 12px; text-align: center; border: 1px solid #E2E8F0;">-</td>
              <td style="padding: 12px; text-align: center; border: 1px solid #E2E8F0;">Mean</td>
              ${displaySubjects.map(subject => `<td style="padding: 12px; text-align: center; border: 1px solid #E2E8F0;">${calculateMeanRow(learners).displayMarks[subject]}</td>`).join('')}
              <td style="padding: 12px; text-align: center; border: 1px solid #E2E8F0;">${calculateMeanRow(learners).total}</td>
              ${stream === 'General' ? '<td style="padding: 12px; text-align: center; border: 1px solid #E2E8F0;">-</td><td style="padding: 12px; text-align: center; border: 1px solid #E2E8F0;">-</td>' : ''}
            </tr>
          </tbody>
        </table>
      </div>
    `;
  };

  const createDocument = async (stream, type, format) => {
    try {
      const learnersToUse = type === 'general' ? learnersByStream['General'] : learnersByStream[stream];
      const fileNameBase = `Performance_${grade}_Term${term}_${getExamDisplayTitle(examType, examName).replace(/\s+/g, '_')}_${type === 'general' ? 'General' : `Stream_${stream}`}`;
      setIsGenerating(prev => ({
        ...prev,
        [type === 'general' ? 'general' : 'streams']: {
          ...prev.streams,
          [stream]: true,
        },
      }));

      if (format === 'pdf') {
        const doc = new jsPDF();
        doc.setFontSize(16);
        doc.text(schoolDetails.schoolName, 105, 20, { align: 'center' });
        doc.setFontSize(12);
        doc.text(`${grade} - Term ${term} - ${getExamDisplayTitle(examType, examName)} - ${type === 'general' ? 'General Results' : `Stream ${stream}`}`, 105, 30, { align: 'center' });
        doc.autoTable({
          startY: 40,
          head: [['Position', 'Student', ...displaySubjects.map(sub => getShortSubjectName(sub)), 'Total', ...(type === 'general' ? ['Stream Position', 'Stream'] : [])]],
          body: [
            ...learnersToUse.map(learner => [
              learner.position,
              `${learner.learnerName}${type === 'general' && learner.stream ? ` (${learner.stream})` : ''}`,
              ...subjects.map(subject => learner.marks[subject] || '-'),
              learner.total,
              ...(type === 'general' ? [
                learnersByStream[learner.stream]?.find(l => l.learnerId === learner.learnerId)?.position || '-',
                learner.stream || 'No Stream',
              ] : []),
            ]),
            ['-', 'Mean', ...subjects.map(subject => calculateMeanRow(learnersToUse).marks[subject]), calculateMeanRow(learnersToUse).total, ...(type === 'general' ? ['-', '-'] : [])],
          ],
        });
        doc.save(`${fileNameBase}.pdf`);
      } else if (format === 'docx') {
        const htmlContent = generateDocxContent(learnersToUse, stream, type);
        const docx = htmlDocx.asBlob(htmlContent);
        const url = URL.createObjectURL(docx);
        const a = document.createElement('a');
        a.href = url;
        a.download = `${fileNameBase}.docx`;
        a.click();
        URL.revokeObjectURL(url);
      } else if (format === 'csv') {
        const csvContent = generateCSVContent(learnersToUse, stream, type);
        const blob = new Blob([csvContent], { type: 'text/csv;charset=utf-8;' });
        const url = URL.createObjectURL(blob);
        const a = document.createElement('a');
        a.href = url;
        a.download = `${fileNameBase}.csv`;
        a.click();
        URL.revokeObjectURL(url);
      }
    } catch (error) {
      console.error(`Error generating ${format} report:`, error);
      alert(`Failed to generate ${format} report. Please try again.`);
    } finally {
      setIsGenerating(prev => ({
        ...prev,
        [type === 'general' ? 'general' : 'streams']: {
          ...prev.streams,
          [stream]: false,
        },
      }));
    }
  };

  const createFullPDF = async (format) => {
    try {
      setIsGenerating(prev => ({ ...prev, general: true }));
      const fileName = `Performance_${grade}_Term${term}_${getExamDisplayTitle(examType, examName).replace(/\s+/g, '_')}`;
      const generalMeanRow = calculateMeanRow(learnersByStream['General']);
      const streamMeanRows = streams.reduce((acc, stream) => {
        acc[stream] = calculateMeanRow(learnersByStream[stream]);
        return acc;
      }, {});

      if (format === 'pdf') {
        const doc = new jsPDF();
        doc.setFontSize(16);
        doc.text(schoolDetails.schoolName, 105, 20, { align: 'center' });
        doc.setFontSize(12);
        doc.text(`${grade} - Term ${term} - ${getExamDisplayTitle(examType, examName)}`, 105, 30, { align: 'center' });

        doc.text('Performance Overview', 20, 40);
        doc.autoTable({
          startY: 45,
          head: [['Subject', 'Average (%)', 'Students']],
          body: subjectAverages.map(item => [getShortSubjectName(item.subject), item.average, item.count]),
        });

        if (streams.length > 0) {
          doc.text('Stream Rankings', 20, doc.lastAutoTable.finalY + 10);
          doc.autoTable({
            startY: doc.lastAutoTable.finalY + 15,
            head: [['Rank', 'Stream', 'Average (%)', 'Students']],
            body: streamAverages.map(({ stream, average, count }, index) => [`#${index + 1}`, stream, average, count]),
          });
        }

        doc.addPage();
        doc.text('Detailed Results - All Students', 20, 20);
        doc.autoTable({
          startY: 25,
          head: [['Position', 'Student', ...displaySubjects.map(sub => getShortSubjectName(sub)), 'Total', 'Stream Position', 'Stream']],
          body: [
            ...learnersByStream['General'].map(learner => [
              learner.position,
              `${learner.learnerName}${learner.stream ? ` (${learner.stream})` : ''}`,
              ...subjects.map(subject => learner.marks[subject] || '-'),
              learner.total,
              learnersByStream[learner.stream]?.find(l => l.learnerId === learner.learnerId)?.position || '-',
              learner.stream || 'No Stream',
            ]),
            ['-', 'Mean', ...subjects.map(subject => generalMeanRow.marks[subject]), generalMeanRow.total, '-', '-'],
          ],
        });

        streams.sort().forEach(stream => {
          doc.addPage();
          doc.text(`Detailed Results - Stream ${stream}`, 20, 20);
          doc.autoTable({
            startY: 25,
            head: [['Position', 'Student', ...displaySubjects.map(sub => getShortSubjectName(sub)), 'Total']],
            body: [
              ...learnersByStream[stream].map(learner => [
                learner.position,
                learner.learnerName,
                ...subjects.map(subject => learner.marks[subject] || '-'),
                learner.total,
              ]),
              ['-', 'Mean', ...subjects.map(subject => streamMeanRows[stream].marks[subject]), streamMeanRows[stream].total],
            ],
          });
        });

        doc.save(`${fileName}.pdf`);
      } else if (format === 'docx') {
        const htmlContent = `
          <div style="font-family: Arial, sans-serif; padding: 30px;">
            <h1 style="text-align: center; font-size: 24px;">${schoolDetails.schoolName}</h1>
            <h2 style="font-size: 20px; margin-bottom: 10px;">${grade} - Term ${term} - ${getExamDisplayTitle(examType, examName)}</h2>
            <h3>Performance Overview</h3>
            <table style="width: 100%; border-collapse: collapse; margin-top: 12px;">
              <thead>
                <tr>
                  <th style="background: #3182CE; color: #FFFFFF; padding: 12px; text-align: center;">Subject</th>
                  <th style="background: #3182CE; color: #FFFFFF; padding: 12px; text-align: center;">Average (%)</th>
                  <th style="background: #3182CE; color: #FFFFFF; padding: 12px; text-align: center;">Students</th>
                </tr>
              </thead>
              <tbody>
                ${subjectAverages.map(item => `
                  <tr>
                    <td style="padding: 12px; text-align: center; border: 1px solid #E2E8F0;">${getShortSubjectName(item.subject)}</td>
                    <td style="padding: 12px; text-align: center; border: 1px solid #E2E8F0;">${item.average}</td>
                    <td style="padding: 12px; text-align: center; border: 1px solid #E2E8F0;">${item.count}</td>
                  </tr>
                `).join('')}
              </tbody>
            </table>
            <h3>Stream Rankings</h3>
            <table style="width: 100%; border-collapse: collapse; margin-top: 12px;">
              <thead>
                <tr>
                  <th style="background: #3182CE; color: #FFFFFF; padding: 12px; text-align: center;">Rank</th>
                  <th style="background: #3182CE; color: #FFFFFF; padding: 12px; text-align: center;">Stream</th>
                  <th style="background: #3182CE; color: #FFFFFF; padding: 12px; text-align: center;">Average (%)</th>
                  <th style="background: #3182CE; color: #FFFFFF; padding: 12px; text-align: center;">Students</th>
                </tr>
              </thead>
              <tbody>
                ${streamAverages.map(({ stream, average, count }, index) => `
                  <tr>
                    <td style="padding: 12px; text-align: center; border: 1px solid #E2E8F0;">#${index + 1}</td>
                    <td style="padding: 12px; text-align: center; border: 1px solid #E2E8F0;">${stream}</td>
                    <td style="padding: 12px; text-align: center; border: 1px solid #E2E8F0;">${average}</td>
                    <td style="padding: 12px; text-align: center; border: 1px solid #E2E8F0;">${count}</td>
                  </tr>
                `).join('')}
              </tbody>
            </table>
            <h3>Detailed Results - All Students</h3>
            <table style="width: 100%; border-collapse: collapse; margin-top: 12px;">
              <thead>
                <tr>
                  <th style="background: #3182CE; color: #FFFFFF; padding: 12px; text-align: center;">Position</th>
                  <th style="background: #3182CE; color: #FFFFFF; padding: 12px; text-align: center;">Student</th>
                  ${displaySubjects.map(subject => `<th style="background: #3182CE; color: #FFFFFF; padding: 12px; text-align: center;">${getShortSubjectName(subject)}</th>`).join('')}
                  <th style="background: #3182CE; color: #FFFFFF; padding: 12px; text-align: center;">Total</th>
                  <th style="background: #3182CE; color: #FFFFFF; padding: 12px; text-align: center;">Stream Position</th>
                  <th style="background: #3182CE; color: #FFFFFF; padding: 12px; text-align: center;">Stream</th>
                </tr>
              </thead>
              <tbody>
                ${learnersByStream['General'].map(learner => `
                  <tr>
                    <td style="padding: 12px; text-align: center; border: 1px solid #E2E8F0;">${learner.position}</td>
                    <td style="padding: 12px; text-align: center; border: 1px solid #E2E8F0;">${learner.learnerName}${learner.stream ? ` (${learner.stream})` : ''}</td>
                    ${subjects.map(subject => `<td style="padding: 12px; text-align: center; border: 1px solid #E2E8F0;">${learner.marks[subject] || '-'}</td>`).join('')}
                    <td style="padding: 12px; text-align: center; border: 1px solid #E2E8F0;">${learner.total}</td>
                    <td style="padding: 12px; text-align: center; border: 1px solid #E2E8F0;">${learnersByStream[learner.stream]?.find(l => l.learnerId === learner.learnerId)?.position || '-'}</td>
                    <td style="padding: 12px; text-align: center; border: 1px solid #E2E8F0;">${learner.stream || 'No Stream'}</td>
                  </tr>
                `).join('')}
                <tr style="background-color: #F0F9FF; font-weight: bold;">
                  <td style="padding: 12px; text-align: center; border: 1px solid #E2E8F0;">-</td>
                  <td style="padding: 12px; text-align: center; border: 1px solid #E2E8F0;">Mean</td>
                  ${displaySubjects.map(subject => `<td style="padding: 12px; text-align: center; border: 1px solid #E2E8F0;">${generalMeanRow.displayMarks[subject]}</td>`).join('')}
                  <td style="padding: 12px; text-align: center; border: 1px solid #E2E8F0;">${generalMeanRow.total}</td>
                  <td style="padding: 12px; text-align: center; border: 1px solid #E2E8F0;">-</td>
                  <td style="padding: 12px; text-align: center; border: 1px solid #E2E8F0;">-</td>
                </tr>
              </tbody>
            </table>
            ${streams.sort().map(stream => `
              <h3>Detailed Results - Stream ${stream}</h3>
              <table style="width: 100%; border-collapse: collapse; margin-top: 12px;">
                <thead>
                  <tr>
                    <th style="background: #3182CE; color: #FFFFFF; padding: 12px; text-align: center;">Position</th>
                    <th style="background: #3182CE; color: #FFFFFF; padding: 12px; text-align: center;">Student</th>
                    ${displaySubjects.map(subject => `<th style="background: #3182CE; color: #FFFFFF; padding: 12px; text-align: center;">${getShortSubjectName(subject)}</th>`).join('')}
                    <th style="background: #3182CE; color: #FFFFFF; padding: 12px; text-align: center;">Total</th>
                  </tr>
                </thead>
                <tbody>
                  ${learnersByStream[stream].map(learner => `
                    <tr>
                      <td style="padding: 12px; text-align: center; border: 1px solid #E2E8F0;">${learner.position}</td>
                      <td style="padding: 12px; text-align: center; border: 1px solid #E2E8F0;">${learner.learnerName}</td>
                      ${subjects.map(subject => `<td style="padding: 12px; text-align: center; border: 1px solid #E2E8F0;">${learner.marks[subject] || '-'}</td>`).join('')}
                      <td style="padding: 12px; text-align: center; border: 1px solid #E2E8F0;">${learner.total}</td>
                    </tr>
                  `).join('')}
                  <tr style="background-color: #F0F9FF; font-weight: bold;">
                    <td style="padding: 12px; text-align: center; border: 1px solid #E2E8F0;">-</td>
                    <td style="padding: 12px; text-align: center; border: 1px solid #E2E8F0;">Mean</td>
                    ${displaySubjects.map(subject => `<td style="padding: 12px; text-align: center; border: 1px solid #E2E8F0;">${streamMeanRows[stream].displayMarks[subject]}</td>`).join('')}
                    <td style="padding: 12px; text-align: center; border: 1px solid #E2E8F0;">${streamMeanRows[stream].total}</td>
                  </tr>
                </tbody>
              </table>
            `).join('')}
          </div>
        `;
        const docx = htmlDocx.asBlob(htmlContent);
        const url = URL.createObjectURL(docx);
        const a = document.createElement('a');
        a.href = url;
        a.download = `${fileName}.docx`;
        a.click();
        URL.revokeObjectURL(url);
      } else if (format === 'csv') {
        const csvContent = generateCSVContent(learnersByStream['General'], 'General', 'general');
        const blob = new Blob([csvContent], { type: 'text/csv;charset=utf-8;' });
        const url = URL.createObjectURL(blob);
        const a = document.createElement('a');
        a.href = url;
        a.download = `${fileName}.csv`;
        a.click();
        URL.revokeObjectURL(url);
      }
    } catch (error) {
      console.error(`Error generating ${format} report:`, error);
      alert(`Failed to generate ${format} report. Please try again.`);
    } finally {
      setIsGenerating(prev => ({ ...prev, general: false }));
    }
  };

  if (isLoading) {
    return (
      <Box sx={{ display: 'flex', justifyContent: 'center', alignItems: 'center', height: '100vh' }}>
        <CircularProgress color="primary" />
        <Typography sx={{ mt: 2, color: '#4A5568' }}>Loading and Analyzing Performance Data...</Typography>
      </Box>
    );
  }

  if (!subjects?.length || !processedLearners?.length) {
    return (
      <Container maxWidth="lg" sx={{ py: 4, bgcolor: '#f8f9fa', minHeight: '100vh', textAlign: 'center' }}>
        <Typography sx={{ color: '#E53E3E' }}>No data available. Please check the input data or try again later.</Typography>
      </Container>
    );
  }

  return (
    <Container maxWidth="lg" sx={{ py: 4, bgcolor: '#f8f9fa', minHeight: '100vh' }}>
      <motion.div initial={{ opacity: 0 }} animate={{ opacity: 1 }} transition={{ duration: 0.5 }}>
        <Typography variant="h4" sx={{ fontWeight: 'bold', color: '#2c3e50', mb: 2, textAlign: 'center' }}>
          {schoolDetails.schoolName} - {grade} - Term {term} - {getExamDisplayTitle(examType, examName)}
        </Typography>
        <Paper sx={{ p: 2, mb: 3, bgcolor: '#fff', border: '1px solid #E2E8F0' }}>
          <Typography variant="h6" sx={{ fontWeight: '600', color: '#2D3748' }}>{schoolDetails.schoolName}</Typography>
          <Typography sx={{ color: '#4A5568' }}><strong>School Code:</strong> {schoolDetails.schoolCode}</Typography>
          <Typography sx={{ color: '#4A5568' }}><strong>Address:</strong> {[schoolDetails.village, schoolDetails.location, schoolDetails.subcounty, schoolDetails.county].filter(Boolean).join(', ')}</Typography>
          <Typography sx={{ color: '#4A5568' }}><strong>Physical Address:</strong> {schoolDetails.address}</Typography>
          <Typography sx={{ color: '#4A5568' }}><strong>Contact:</strong> {schoolDetails.phoneNumber}</Typography>
          <Typography sx={{ color: '#4A5568' }}><strong>Email:</strong> {schoolDetails.email}</Typography>
          <Typography sx={{ color: '#4A5568' }}><strong>Website:</strong> {schoolDetails.website}</Typography>
        </Paper>

        {/* Performance Overview */}
        <Paper sx={{ p: 3, mb: 3, bgcolor: '#fff', border: '1px solid #E2E8F0' }}>
          <Typography variant="h5" sx={{ fontWeight: '600', color: '#2D3748', mb: 2 }}>Performance Overview</Typography>
          {streams.length > 0 && (
            <Box sx={{ mb: 3, p: 2, bgcolor: '#F7FAFC', borderRadius: 2, border: '1px solid #E2E8F0' }}>
              <Typography sx={{ fontWeight: '500', color: '#4A5568', mb: 2 }}>Stream Rankings</Typography>
              {streamAverages.map(({ stream, average, count }, index) => (
                <Box key={stream} sx={{ display: 'flex', alignItems: 'center', py: 1, borderBottom: '1px solid #E2E8F0' }}>
                  <Typography sx={{ width: 60, fontWeight: '600', color: '#2D3748' }}>#{index + 1}</Typography>
                  <Typography sx={{ flex: 1, fontWeight: '500', color: '#2D3748' }}>{stream}</Typography>
                  <Typography sx={{ width: 70, textAlign: 'right', fontWeight: '600', color: '#3182CE' }}>{average}%</Typography>
                  <Typography sx={{ width: 80, textAlign: 'right', color: '#718096', fontSize: '0.85rem' }}>({count} students)</Typography>
                </Box>
              ))}
            </Box>
          )}
          <Box sx={{ mb: 3 }}>
            <Typography sx={{ fontWeight: '500', color: '#4A5568', mb: 2 }}>Subject Averages</Typography>
            <Box sx={{ maxWidth: 400, mx: 'auto' }}>
              <Pie data={pieData} options={{ plugins: { legend: { position: 'bottom' } } }} />
            </Box>
          </Box>
          <Box>
            <Typography sx={{ fontWeight: '500', color: '#4A5568', mb: 2 }}>Comparison of Averages</Typography>
            <Bar data={barData} options={{ plugins: { legend: { display: false } }, scales: { y: { beginAtZero: true, title: { display: true, text: 'Average (%)' } } } }} />
          </Box>
        </Paper>

        {/* Subject Analysis by Stream */}
        {streams.length > 0 && (
          <Paper sx={{ p: 3, mb: 3, bgcolor: '#fff', border: '1px solid #E2E8F0' }}>
            <Typography variant="h5" sx={{ fontWeight: '600', color: '#2D3748', mb: 2 }}>Subject Analysis by Stream</Typography>
            {displaySubjects.map(subject => (
              <Accordion key={subject} sx={{ mb: 2, bgcolor: '#fff', border: '1px solid #E2E8F0' }}>
                <AccordionSummary expandIcon={<ExpandMore />}>
                  <Typography sx={{ fontWeight: '600', color: '#2D3748' }}>{getShortSubjectName(subject)}</Typography>
                </AccordionSummary>
                <AccordionDetails>
                  <Box sx={{ maxWidth: 400, mx: 'auto', mb: 2 }}>
                    <Pie data={prepareSubjectComparisonData(subject)} options={{ plugins: { legend: { position: 'bottom' } } }} />
                  </Box>
                  <TableContainer>
                    <Table>
                      <TableHead>
                        <TableRow sx={{ bgcolor: '#E2E8F0' }}>
                          <TableCell sx={{ fontWeight: '600', color: '#2D3748' }}>Stream</TableCell>
                          <TableCell sx={{ fontWeight: '600', color: '#2D3748' }}>Average</TableCell>
                          <TableCell sx={{ fontWeight: '600', color: '#2D3748' }}>Students</TableCell>
                        </TableRow>
                      </TableHead>
                      <TableBody>
                        {streams.sort().map(stream => {
                          const streamAvg = subjectAveragesByStream[stream].find(item => item.subject === subject);
                          return (
                            <TableRow key={stream}>
                              <TableCell>{stream}</TableCell>
                              <TableCell>{streamAvg?.average || 0}</TableCell>
                              <TableCell>{streamAvg?.count || 0}</TableCell>
                            </TableRow>
                          );
                        })}
                      </TableBody>
                    </Table>
                  </TableContainer>
                </AccordionDetails>
              </Accordion>
            ))}
          </Paper>
        )}

        {/* Top Performers */}
        <Paper sx={{ p: 3, mb: 3, bgcolor: '#fff', border: '1px solid #E2E8F0' }}>
          <Typography variant="h5" sx={{ fontWeight: '600', color: '#2D3748', mb: 2 }}>Top Performers</Typography>
          {topPerformers.map(performer => (
            <Box key={performer.learnerId} sx={{ p: 2, mb: 2, bgcolor: '#F7FAFC', borderRadius: 2, borderLeft: '4px solid #3182CE' }}>
              <Typography
                sx={{ fontWeight: '600', color: '#2D3748', cursor: 'pointer', '&:hover': { textDecoration: 'underline' } }}
                onClick={() => navigate('/teacher/student-detailed-analysis', { state: { learnerId: performer.learnerId, learnerName: performer.learnerName, schoolId, grade } })}
              >
                {performer.learnerName}
              </Typography>
              <Typography sx={{ color: '#3182CE', fontWeight: '500' }}>
                Position: {performer.position} | Total: {performer.total} marks | Mean: {performer.mean}%{performer.stream && ` | Stream: ${performer.stream}`}
              </Typography>
              <Box sx={{ display: 'flex', flexWrap: 'wrap', gap: 1 }}>
                {displaySubjects.map((displaySubject, index) => (
                  <Chip key={displaySubject} label={`${getShortSubjectName(displaySubject)}: ${performer.marks[subjects[index]] || 'N/A'}`} sx={{ bgcolor: '#e0e0e0', fontSize: '0.75rem' }} />
                ))}
              </Box>
            </Box>
          ))}
        </Paper>

        {/* Top Students by Subject */}
        <Paper sx={{ p: 3, mb: 3, bgcolor: '#fff', border: '1px solid #E2E8F0' }}>
          <Box sx={{ display: 'flex', alignItems: 'center', mb: 2 }}>
            <Typography variant="h5" sx={{ fontWeight: '600', color: '#2D3748', mr: 2 }}>Top Students by Subject</Typography>
            <FormControl sx={{ minWidth: 120 }}>
              <InputLabel>Top N</InputLabel>
              <Select
                value={topN}
                label="Top N"
                onChange={(e) => setTopN(e.target.value)}
              >
                <MenuItem value={1}>Top 1</MenuItem>
                <MenuItem value={2}>Top 2</MenuItem>
                <MenuItem value={3}>Top 3</MenuItem>
              </Select>
            </FormControl>
          </Box>
          {displaySubjects.map(subject => (
            <Box key={subject} sx={{ mb: 2, p: 2, bgcolor: '#fff', border: '1px solid #E2E8F0', borderRadius: 2 }}>
              <Typography sx={{ fontWeight: '600', color: '#2D3748', mb: 1 }}>{getShortSubjectName(subject)}</Typography>
              {topStudentsBySubject[subject].map((student, index) => (
                <Typography
                  key={index}
                  sx={{ color: '#3182CE', fontWeight: '500', cursor: 'pointer', '&:hover': { textDecoration: 'underline' } }}
                  onClick={() => navigate('/teacher/student-detailed-analysis', { state: { learnerId: student.learnerId, learnerName: student.name, schoolId, grade } })}
                >
                  {student.name}{student.stream && ` (${student.stream})`}: {student.score}%
                </Typography>
              ))}
            </Box>
          ))}
        </Paper>

        {/* Detailed Results */}
        <Paper sx={{ p: 3, mb: 3, bgcolor: '#fff', border: '1px solid #E2E8F0' }}>
          <Typography variant="h5" sx={{ fontWeight: '600', color: '#2D3748', mb: 2 }}>Detailed Results</Typography>
          <Typography sx={{ fontWeight: '600', color: '#2D3748', mb: 1 }}>General Results (All Students)</Typography>
          <TableContainer>
            <Table>
              <TableHead>
                <TableRow sx={{ bgcolor: '#3182CE' }}>
                  <TableCell sx={{ color: '#fff', fontWeight: '600', width: 60 }}>Pos</TableCell>
                  <TableCell sx={{ color: '#fff', fontWeight: '600' }}>Student</TableCell>
                  {displaySubjects.map(subject => (
                    <TableCell key={subject} sx={{ color: '#fff', fontWeight: '600' }}>{getShortSubjectName(subject)}</TableCell>
                  ))}
                  <TableCell sx={{ color: '#fff', fontWeight: '600' }}>Total</TableCell>
                  <TableCell sx={{ color: '#fff', fontWeight: '600' }}>Stream Pos</TableCell>
                  <TableCell sx={{ color: '#fff', fontWeight: '600' }}>Stream</TableCell>
                </TableRow>
              </TableHead>
              <TableBody>
                {learnersByStream['General'].map(learner => (
                  <TableRow key={learner.learnerId}>
                    <TableCell>{learner.position}</TableCell>
                    <TableCell>
                      <Typography
                        sx={{ cursor: 'pointer', '&:hover': { textDecoration: 'underline' } }}
                        onClick={() => navigate('/teacher/student-detailed-analysis', { state: { learnerId: learner.learnerId, learnerName: learner.learnerName, schoolId, grade } })}
                      >
                        {learner.learnerName}{learner.stream && ` (${learner.stream})`}
                      </Typography>
                    </TableCell>
                    {subjects.map(subject => (
                      <TableCell key={subject}>{learner.marks[subject] || '-'}</TableCell>
                    ))}
                    <TableCell sx={{ fontWeight: '600' }}>{learner.total}</TableCell>
                    <TableCell>{learnersByStream[learner.stream]?.find(l => l.learnerId === learner.learnerId)?.position || '-'}</TableCell>
                    <TableCell>{learner.stream || 'No Stream'}</TableCell>
                  </TableRow>
                ))}
                <TableRow sx={{ bgcolor: '#F0F9FF' }}>
                  <TableCell sx={{ fontWeight: '600' }}>-</TableCell>
                  <TableCell sx={{ fontWeight: '600' }}>Mean</TableCell>
                  {displaySubjects.map(subject => (
                    <TableCell key={subject} sx={{ fontWeight: '600' }}>
                      {subjectAverages.find(s => s.subject === subject)?.average || 0}
                    </TableCell>
                  ))}
                  <TableCell sx={{ fontWeight: '700', color: '#3182CE' }}>
                    {Number((processedLearners.reduce((sum, learner) => sum + learner.total, 0) / (processedLearners.length || 1)).toFixed(2))}
                  </TableCell>
                  <TableCell sx={{ fontWeight: '600' }}>-</TableCell>
                  <TableCell sx={{ fontWeight: '600' }}>-</TableCell>
                </TableRow>
              </TableBody>
            </Table>
          </TableContainer>
          <Button
            variant="contained"
            sx={{ bgcolor: '#3182CE', mt: 2 }}
            onClick={() => createDocument('General', 'general', 'pdf')}
            disabled={isGenerating.general}
          >
            {isGenerating.general ? <CircularProgress size={24} color="inherit" /> : 'Download General Results (PDF)'}
          </Button>
          <Button
            variant="outlined"
            sx={{ ml: 1, mt: 2 }}
            onClick={() => createDocument('General', 'general', 'docx')}
            disabled={isGenerating.general}
          >
            {isGenerating.general ? <CircularProgress size={24} color="inherit" /> : 'Download (DOCX)'}
          </Button>
          <Button
            variant="outlined"
            sx={{ ml: 1, mt: 2 }}
            onClick={() => createDocument('General', 'general', 'csv')}
            disabled={isGenerating.general}
          >
            {isGenerating.general ? <CircularProgress size={24} color="inherit" /> : 'Download (CSV)'}
          </Button>

          {streams.sort().map(stream => (
            <Box key={stream} sx={{ mt: 3 }}>
              <Typography sx={{ fontWeight: '600', color: '#2D3748', mb: 1 }}>Stream: {stream}</Typography>
              <TableContainer>
                <Table>
                  <TableHead>
                    <TableRow sx={{ bgcolor: '#3182CE' }}>
                      <TableCell sx={{ color: '#fff', fontWeight: '600', width: 60 }}>Pos</TableCell>
                      <TableCell sx={{ color: '#fff', fontWeight: '600' }}>Student</TableCell>
                      {displaySubjects.map(subject => (
                        <TableCell key={subject} sx={{ color: '#fff', fontWeight: '600' }}>{getShortSubjectName(subject)}</TableCell>
                      ))}
                      <TableCell sx={{ color: '#fff', fontWeight: '600' }}>Total</TableCell>
                    </TableRow>
                  </TableHead>
                  <TableBody>
                    {learnersByStream[stream].map(learner => (
                      <TableRow key={learner.learnerId}>
                        <TableCell>{learner.position || '-'}</TableCell>
                        <TableCell>
                          <Typography
                            sx={{ cursor: 'pointer', '&:hover': { textDecoration: 'underline' } }}
                            onClick={() => navigate('/teacher/student-detailed-analysis', { state: { learnerId: learner.learnerId, learnerName: learner.learnerName, schoolId, grade } })}
                          >
                            {String(learner.learnerName || 'N/A')}
                          </Typography>
                        </TableCell>
                        {subjects.map(subject => (
                          <TableCell key={subject}>{String(learner.marks[subject] ?? '-')}</TableCell>
                        ))}
                        <TableCell sx={{ fontWeight: '600' }}>{String(learner.total ?? '-')}</TableCell>
                      </TableRow>
                    ))}
                    <TableRow sx={{ bgcolor: '#F0F9FF' }}>
                      <TableCell sx={{ fontWeight: '600' }}>-</TableCell>
                      <TableCell sx={{ fontWeight: '600' }}>Mean</TableCell>
                      {displaySubjects.map(subject => (
                        <TableCell key={subject} sx={{ fontWeight: '600' }}>
                          {subjectAveragesByStream[stream].find(s => s.subject === subject)?.average || 0}
                        </TableCell>
                      ))}
                      <TableCell sx={{ fontWeight: '700', color: '#3182CE' }}>
                        {Number((learnersByStream[stream].reduce((sum, learner) => sum + (learner.total || 0), 0) / (learnersByStream[stream].length || 1)).toFixed(2)) || 0}
                      </TableCell>
                    </TableRow>
                  </TableBody>
                </Table>
              </TableContainer>
              <Button
                variant="contained"
                sx={{ bgcolor: '#3182CE', mt: 2 }}
                onClick={() => createDocument(stream, 'stream', 'pdf')}
                disabled={isGenerating.streams[stream]}
              >
                {isGenerating.streams[stream] ? <CircularProgress size={24} color="inherit" /> : 'Download Stream Report (PDF)'}
              </Button>
              <Button
                variant="outlined"
                sx={{ ml: 1, mt: 2 }}
                onClick={() => createDocument(stream, 'stream', 'docx')}
                disabled={isGenerating.streams[stream]}
              >
                {isGenerating.streams[stream] ? <CircularProgress size={24} color="inherit" /> : 'Download (DOCX)'}
              </Button>
              <Button
                variant="outlined"
                sx={{ ml: 1, mt: 2 }}
                onClick={() => createDocument(stream, 'stream', 'csv')}
                disabled={isGenerating.streams[stream]}
              >
                {isGenerating.streams[stream] ? <CircularProgress size={24} color="inherit" /> : 'Download (CSV)'}
              </Button>
            </Box>
          ))}
        </Paper>

        <Button
          variant="contained"
          sx={{ bgcolor: '#3182CE', mt: 2 }}
          onClick={() => createFullPDF('pdf')}
          disabled={isGenerating.general}
        >
          {isGenerating.general ? <CircularProgress size={24} color="inherit" /> : 'Download Full Report (PDF)'}
        </Button>
        <Button
          variant="outlined"
          sx={{ ml: 1, mt: 2 }}
          onClick={() => createFullPDF('docx')}
          disabled={isGenerating.general}
        >
          {isGenerating.general ? <CircularProgress size={24} color="inherit" /> : 'Download Full Report (DOCX)'}
        </Button>
        <Button
          variant="outlined"
          sx={{ ml: 1, mt: 2 }}
          onClick={() => createFullPDF('csv')}
          disabled={isGenerating.general}
        >
          {isGenerating.general ? <CircularProgress size={24} color="inherit" /> : 'Download Full Report (CSV)'}
        </Button>
      </motion.div>
    </Container>
  );
};

export default PerformanceAnalysis;